import { useQuery } from '@apollo/client';
import TeamItemDetailsQuery from '@apps/www/src/www/queries/teamItemByShortIDQuery';
import SVA from '@pkgs/shared-client/components/SVA';
import SVAvatarsStack from '@pkgs/shared-client/components/SVAvatarsStack';
import SVImage from '@pkgs/shared-client/components/SVImage';
import SVLink from '@pkgs/shared-client/components/SVLink';
import { isTouch } from '@pkgs/shared-client/helpers/dom';
import plural from '@pkgs/shared-client/helpers/plural';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import SVCommentIconSVG from '@pkgs/shared-client/img/icon-comments-ballon-inlined.svg';
import { type GridHoverOverlayUISize } from '@pkgs/shared/constants';
import AssetType from '@pkgs/shared/enums/AssetType';
import memoize from 'lodash/memoize';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { LikeItem } from './SVGrid';
import SVGridItem from './SVGridItem';

const CARD_MIN_RATIO = 0.3; // height is at least 30% of the width
const CARD_BOTTOM_PEEK = 80; // 80px of peek for next card
const CARD_FOOTER_HEIGHT = 44; // height + bottom margin

const CARD_BOTTOM_PADDING = 90; // 90px of bottom padding
export const CARD_BOTTOM_PADDING_CLASSES = 'pb-[90px]'; // keep this in sync with `CARD_BOTTOM_PADDING`
export const CARD_WIDTH_CLASSES = '-lg:w-[75dvw] -md:w-[70dvw] -sm:w-[85dvw] w-[55dvw]';

export const createCardElementIDFromShortID = memoize(
	(shortID: string) => `grid-card-item-${shortID}`,
);

const formatSavedMessage = (users: { name: string; url?: string }[]) => {
	if (!users.length) return null;

	return (
		<>
			Saved by{' '}
			<SVA
				Component={SVLink}
				to={users[0].url ?? ''}
				className="text-gray-400 hover:text-gray-300"
				onClick={(e) => e.stopPropagation()}
			>
				{users[0].name}
			</SVA>{' '}
			{users.length === 2 && (
				<>
					and{' '}
					<SVA
						Component={SVLink}
						to={users[1].url ?? ''}
						className="text-gray-400 hover:text-gray-300"
						onClick={(e) => e.stopPropagation()}
					>
						{users[1].name}
					</SVA>
				</>
			)}
			{users.length > 2 && <>and {users.length - 1} others</>}
		</>
	);
};

const _LoadingSkeleton = () => {
	return (
		<div className="flex items-center justify-between">
			<div className="flex items-center">
				<div className="h-6 w-6 animate-pulse rounded-full bg-gray-700" />
				<div className="-ml-2 h-6 w-6 animate-pulse rounded-full bg-gray-700" />
				<div className="ml-2 h-4 w-[10vw] animate-pulse rounded-md bg-gray-700" />
			</div>
			<div className="h-4 w-[7vw] animate-pulse rounded-md bg-gray-700" />
		</div>
	);
};

const SVGridCardItem = ({
	item,
	isStatic = false,
	renderHoverOverlay,
	className,
	routerPathname,
	routerQuery,
	url,
	onItemClick,
	height,
}: {
	item: LikeItem;
	isStatic?: boolean;
	isSelected?: boolean;
	renderHoverOverlay?: (props: {
		uiSize: GridHoverOverlayUISize;
		canMove: boolean;
	}) => React.ReactNode;
	autoPlayGIFs?: boolean;
	routerPathname?: string;
	routerQuery?: ParsedUrlQuery;
	url?: string;
	className?: string;
	onItemClick?: (itemID: string, event: React.UIEvent) => void;
	height: number;
}) => {
	const [isOver, setIsOver] = useState(false);
	const router = useRouter();
	const itemID = item._id;
	const itemShortID = item.shortID;

	const itemProps = SVGridItem.itemToProps(item);
	const isVideo = item.asset.type === AssetType.VIDEO;

	const { data: itemDetails, loading } = useQuery(TeamItemDetailsQuery, {
		variables: { shortID: itemShortID },
		skip: !itemShortID,
	});

	const handleMouseEnter = useEventCallback(() => {
		if (isStatic || isTouch()) return;
		setIsOver(true);
	});

	const handleMouseLeave = useEventCallback(() => {
		if (isStatic || isTouch()) return;
		setIsOver(false);
	});

	const handleClick = useEventCallback((event: React.UIEvent) => {
		if (onItemClick) {
			onItemClick(itemID, event);
		}
	});

	const handleCommentsClick = useEventCallback((event: React.UIEvent) => {
		event.stopPropagation();
		event.preventDefault();
		router.push(
			{
				pathname: routerPathname,
				query: { ...routerQuery, itemShortID, commentMode: 'on' },
			},
			item.url,
			{ scroll: false, shallow: true },
		);
	});

	const linkTo = {
		pathname: routerPathname,
		query: { ...routerQuery, itemShortID },
	};
	const linkAs = url;

	return (
		<div
			id={createCardElementIDFromShortID(itemShortID)}
			className={twMerge(CARD_WIDTH_CLASSES, 'flex flex-col overflow-hidden', className)}
		>
			<SVA
				to={linkTo}
				as={linkAs}
				onClick={handleClick}
				Component={SVLink}
				className="relative flex min-h-0 flex-1 cursor-pointer"
				data-item-id={itemID}
				scroll={false}
				prefetch={false}
				shallow={true}
				style={{ height: `${height - CARD_FOOTER_HEIGHT - CARD_BOTTOM_PADDING}px` }}
			>
				<div
					className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[4px] bg-gray-950"
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					style={{ height: `${height - CARD_FOOTER_HEIGHT - CARD_BOTTOM_PADDING}px` }}
				>
					<div
						className="flex h-full w-full items-center justify-center overflow-hidden"
						style={{ height: `${height - CARD_FOOTER_HEIGHT - CARD_BOTTOM_PADDING}px` }}
					>
						{isVideo ? (
							<video
								className="w-full object-cover"
								src={itemProps.original}
								playsInline
								autoPlay
								loop
								muted
								style={{
									backgroundColor: itemProps.color || 'transparent',
									height: `${
										height - CARD_FOOTER_HEIGHT - CARD_BOTTOM_PADDING
									}px`,
								}}
							/>
						) : (
							<SVImage
								src={itemProps.original}
								alt=""
								className="w-full object-cover"
								loading="lazy"
								style={{
									backgroundColor: itemProps.color || 'transparent',
									height: `${
										height - CARD_FOOTER_HEIGHT - CARD_BOTTOM_PADDING
									}px`,
								}}
							/>
						)}
					</div>

					{isOver && renderHoverOverlay && (
						<div className="absolute inset-0">
							{renderHoverOverlay({
								uiSize: 'medium',
								canMove: false,
							})}
						</div>
					)}
				</div>
			</SVA>
			<div className="default flex-shrink-0 py-2.5">
				{loading ? (
					<_LoadingSkeleton />
				) : (
					<div className="-sm:space-y-3 -sm:flex-col -sm:items-start flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<SVAvatarsStack
								users={itemDetails?.itemByShortID?.asset?.teamUsers ?? []}
								showAdd={false}
								showDropdown={true}
								visibleCount={3}
							/>
							<div className="text-sm text-gray-500">
								{formatSavedMessage(
									itemDetails?.itemByShortID?.asset?.teamUsers || [],
								)}
							</div>
						</div>
						<div
							className="flex cursor-pointer items-center space-x-2 text-sm text-gray-400 hover:text-gray-300"
							onClick={handleCommentsClick}
						>
							<SVCommentIconSVG />

							<div>
								{plural(
									itemDetails?.itemByShortID?.asset?.commentsCount ?? 0,
									'Comment',
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

SVGridCardItem.DetailSkeleton = _LoadingSkeleton;

function calculatePreviewHeight(
	cardMetrics: {
		windowHeight: number;
		top: number;
		width: number;
	},
	item: LikeItem,
) {
	const ratio = item.asset.image.ratio;
	const idealHeight = cardMetrics.width * ratio;

	const previewMaxHeight =
		cardMetrics.windowHeight -
		cardMetrics.top -
		CARD_BOTTOM_PEEK -
		CARD_FOOTER_HEIGHT -
		CARD_BOTTOM_PADDING;
	const constrainedHeight = Math.min(idealHeight, previewMaxHeight);

	// Don't let the ratio be smaller than .3
	const minHeight = cardMetrics.width * CARD_MIN_RATIO;
	return Math.floor(Math.max(constrainedHeight, minHeight));
}

// Given a card container width, calculates the full height of the card
SVGridCardItem.calculateHeight = (
	cardMetrics: {
		windowHeight: number;
		top: number;
		width: number;
	},
	item: LikeItem,
) => {
	const previewHeight = calculatePreviewHeight(cardMetrics, item);

	return previewHeight + CARD_FOOTER_HEIGHT + CARD_BOTTOM_PADDING;
};

export default SVGridCardItem;
